import type { FrontDoors, PipelineTile } from "../api";
import { FrontDoorTile } from "./FrontDoorTile";
import { GateMarker } from "./GateMarker";
import { isGateTile } from "./laneOrder";
import { OutputTile } from "./OutputTile";
import { StepTile } from "./StepTile";

/** Muted subtitle shown after the bold step-range/title eyebrow (UX-mockup prototype v2, `.lh`). */
const SUBTITLE: Record<string, string> = {
  "Front doors": "where needs come in this week",
  "Needs and routing": "what each need becomes",
  Cortex: "science research track",
  Protex: "CX facts and policy track",
  Vox: "content track",
};

function stepRange(tiles: PipelineTile[]): string | null {
  const steps = tiles.map((t) => t.step).filter((s): s is number => s != null);
  if (steps.length === 0) return null;
  const min = Math.min(...steps);
  const max = Math.max(...steps);
  return min === max ? `${min}` : `${min}–${max}`;
}

/**
 * One pipeline track — a lane's tiles placed by `step`, with the lane's gates
 * (`step: null`, key `g`/`pg`) rendered as a `GateMarker` right after the step
 * they belong after (caller pre-orders via `orderLaneWithGates`), joined by
 * `›` flow arrows inside a bordered lane box (UX-mockup prototype v2, frame
 * 1). `tiles` is expected pre-sorted/pre-filtered to this lane. In `outputs`
 * mode every tile is an Outputs-lane tile (never a gate, even though its
 * `step` is also null) — `o1`-`o5` are outputs, not gates.
 */
export function Track({
  title,
  tiles,
  frontDoors,
  onOpen,
  note,
  branchTile,
  outputs,
  noun = "week",
}: {
  title: string;
  tiles: PipelineTile[];
  /** Only meaningful for the `frontdoor` lane. */
  frontDoors?: FrontDoors;
  onOpen: (stageKey: string) => void;
  /** e.g. the "from 11:" branch caption for Vox. */
  note?: string;
  /** The tile shown on the branch line named by `note` (e.g. o4 blueprint-publish). */
  branchTile?: PipelineTile;
  /** Render as a compact single-line row inside the shared Outputs lane, no border of its own. */
  outputs?: boolean;
  /** "week" or "month" — cvce-pipeline-4's grain toggle, threaded to each tile's "No data this …" note. */
  noun?: "week" | "month";
}) {
  const range = stepRange(tiles);
  const subtitle = SUBTITLE[title];

  const row = (
    <div className="flex flex-wrap items-stretch gap-1.5" data-track={title}>
      {tiles.map((tile, i) => {
        const isGate = !outputs && isGateTile(tile);
        return (
          <span key={tile.stage_key} className="flex items-stretch gap-1.5">
            {i > 0 && (
              <span aria-hidden className="self-center text-base leading-none text-ks-grey-3">
                {"›"}
              </span>
            )}
            {outputs ? (
              <OutputTile tile={tile} onOpen={onOpen} noun={noun} />
            ) : isGate ? (
              <GateMarker gate={tile} onOpen={onOpen} />
            ) : frontDoors ? (
              <FrontDoorTile tile={tile} frontDoors={frontDoors} onOpen={onOpen} noun={noun} />
            ) : (
              <StepTile tile={tile} onOpen={onOpen} noun={noun} />
            )}
          </span>
        );
      })}
    </div>
  );

  if (outputs) {
    return (
      <div className="flex items-start gap-2">
        <span className="w-16 shrink-0 pt-1.5 text-[11px] font-bold uppercase tracking-wide text-ks-purple">
          {title}
        </span>
        {row}
      </div>
    );
  }

  return (
    <section
      aria-labelledby={`track-${title}`}
      className="flex flex-col gap-2 rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3"
    >
      <h3 id={`track-${title}`} className="flex items-baseline gap-2 text-[13px]">
        <span className="font-bold text-ks-purple">{range ? `${range} · ${title}` : title}</span>
        {subtitle && <span className="text-ks-grey-1">{subtitle}</span>}
      </h3>
      {row}
      {note && branchTile && (
        <div className="ml-6 flex items-center gap-2 text-[11px] text-ks-grey-1">
          <span aria-hidden>{"└"}</span>
          <span>{note}</span>
          <OutputTile tile={branchTile} onOpen={onOpen} noun={noun} />
        </div>
      )}
    </section>
  );
}
